/** @jsxImportSource @emotion/react */
import solidArrow from '../assets/icons/downArrowSolid.svg';
import * as React from 'react';
import * as R from 'ramda';
import { css } from '@emotion/react';
import {PRODUCT_MAPPING_ROUTE, FAQ_ROUTE, HELP_AND_TRAINING_ROUTE, RELEASE_NOTES_ROUTE, SYSTEM_ADMIN_ROUTE, USER_MANAGEMENT_ROUTE, VALIDATION_CONFIGURATION_ROUTE, REPORTS_ROUTE, PRODUCT_ADMINISTRATION_ROUTE} from '../core/routes';
import FloatingMenu from '../layout/menu/FloatingMenu';
import { UNSELECTABLE_TEXT } from '../styles/styles';
import {useSelector} from 'react-redux';
import {IAppState} from '../core/redux/IAppState';
import {MenuEntry} from '../layout/menu/menuEntry';
import {useAuth0} from '@auth0/auth0-react';
import {redirectUri} from '../index';
import { getResource } from '../language/resources/getResource';
import { useNavigate } from 'react-router-dom';
import { useCallback } from 'react';

const floatingMenuArrow = css`
  width: 19px; /* Odd dimensions size image to the height of a lower-case letter @ 2rem */
  height: 9.5px;
  padding-left: 5px;
  padding-right: 5px;
  padding-top: 5px;
  ${UNSELECTABLE_TEXT};
`;

/**
 * HeaderMenu
 * A dropdown menu primarily used on the Resort Tools header when the user click on the username.
 * Displays a menu of options available dependent upon their role.
 */
export const HeaderMenu: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth0();
  const config = useSelector((s: IAppState) => s.resortToolsConfig);
  const userRole = useSelector((s: IAppState) => s.userState?.role ?? 'agent');
  const resortMappings = useSelector(
    (s: IAppState) => s.resortMappingHistories.productMappings
  );
  const resources = useSelector((s: IAppState) => s.locale);

  const go = useCallback((to: string) => navigate(to),[navigate]);

  const canViewReports = R.includes(userRole, config!.enableHotlistReport) ||
                         R.includes(userRole, config!.enableGuestHydrationReport) ||
                         R.includes(userRole, config!.enableWeeklyVisitorReport)

  const menuItems: MenuEntry[] = React.useMemo(() => {
    const items: MenuEntry[] = [
      {
        key: 'System Administration',
        text: getResource(resources, 'HEADER_MENU_SYSTEM_ADMIN'),
        onClick: () => go(SYSTEM_ADMIN_ROUTE),
        hide: !R.includes(userRole, config!.manageSystemAdmin),
      },
    {
        key: 'Product Administration',
        text: getResource(resources, 'HEADER_MENU_PRODUCT_ADMINISTRATION'),
        onClick: () => go(PRODUCT_ADMINISTRATION_ROUTE),
        hide: !R.includes(userRole, config!.manageProductAdministration),
      },
      {
        key: 'Validation Configuration',
        text: getResource(resources, 'HEADER_MENU_VALIDATION_CONFIG'),
        onClick: () => go(VALIDATION_CONFIGURATION_ROUTE),
        hide: !R.includes(userRole, config!.manageValidationConfiguration),
      },
      {
        key: 'User Management',
        text: getResource(resources, 'HEADER_MENU_USER_MANAGEMENT'),
        onClick: () => go(USER_MANAGEMENT_ROUTE),
        hide: !R.includes(userRole, config!.manageUsers),
      },
      {
        key: 'Reports',
        text: getResource(resources, 'HEADER_MENU_REPORTS'),
        onClick: () => go(REPORTS_ROUTE),
        hide: !canViewReports,
      },
      {
        key: 'Help and Training',
        text: getResource(resources, 'HEADER_MENU_HELP_AND_TRAINING'),
        onClick: () => go(HELP_AND_TRAINING_ROUTE),
        // Temporary gate while the feature is fleshed out — remove the hide to restore all-roles visibility (IRI-14787 AC1)
        hide: !R.includes(userRole, config!.manageHelpDocs),
      },
      {
        key: 'FAQ',
        text: getResource(resources, 'HEADER_MENU_FAQ'),
        onClick: () => go(FAQ_ROUTE),
      },
      {
        key: 'Release Notes',
        text: getResource(resources, 'HEADER_MENU_RELEASE_NOTES'),
        onClick: () => go(RELEASE_NOTES_ROUTE),
      },
      {
        key: 'Signout',
        text: getResource(resources, 'HEADER_MENU_SIGNOUT'),
        onClick: () => logout({ logoutParams: { returnTo: redirectUri } }),
      },
    ];

    // Insert Product Mapping after "Product Administration" if enabled
    if (
      R.includes(userRole, config!.enableProductMapping) &&
      resortMappings != null &&
      resortMappings.length > 0
    ) {
      items.splice(2, 0, {
        key: 'Product Mapping',
        text: getResource(resources, 'HEADER_MENU_PRODUCT_MAPPING'),
        onClick: () => go(PRODUCT_MAPPING_ROUTE),
        hide: false,
      });
    }

    return items;
  }, [resources, userRole, config, canViewReports, resortMappings, go, logout]);

  return (
    <FloatingMenu
      menuZIndex={200}
      menuItems={menuItems}
      children={<img alt="arrow" src={solidArrow} css={floatingMenuArrow} />}
    />
  );
};
